"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useTranslations } from "next-intl";
import { Cookie } from "lucide-react";
import { memo } from "react";

import { Link } from "@/i18n/navigation";
import { useCookieConsent } from "@/shared/lib/hooks/use-cookie-consent";
import { Button } from "@/shared/ui/buttons/button";

/**
 * Баннер согласия на куки.
 *
 * Технические куки (сессия sid, CSRF, язык, тема) ставятся всегда. Umami на
 * нашем сервере cookieless и согласия не требует. Яндекс.Метрика (и GTM, если
 * его включат в analytics-ids.ts) ставят куки, поэтому грузятся только после «Принять»
 * (KAN-1198, см. third-party-analytics.tsx). «Только необходимые» — равноценная
 * кнопка, а не спрятанная ссылка: отказ должен быть так же прост, как согласие.
 */
export const CookieConsent = memo(function CookieConsent() {
  const t = useTranslations("Common.cookies");
  const { isVisible, acceptAll, rejectAll } = useCookieConsent();

  return (
    // initial={false}: баннер в SSR-HTML виден сразу. Появление с opacity 0
    // браузер не засчитал бы как отрисовку, и LCP снова ждал бы JS (KAN-1244).
    // Слой уведомлений (100): launcher чата (60) не перекрывает выбор куки.
    <AnimatePresence initial={false}>
      {isVisible && (
        <motion.div
          role="region"
          data-cookie-banner=""
          aria-label={t("title")}
          className="fixed inset-x-3 bottom-3 z-100 sm:inset-x-auto sm:left-4 sm:bottom-4 sm:max-w-sm"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 12 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          <div className="flex items-start gap-3 rounded-2xl border border-border bg-card/95 p-3.5 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-card/85">
            <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-muted">
              <Cookie className="h-4 w-4 text-muted-foreground" aria-hidden />
            </span>

            <div className="min-w-0 flex-1">
              <p className="text-sm leading-snug text-foreground">
                {t("text")}
              </p>
              <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-2">
                <Button size="sm" className="h-8 px-3" onClick={acceptAll}>
                  {t("accept")}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 px-3"
                  onClick={rejectAll}
                >
                  {t("rejectAll")}
                </Button>
                <Link
                  href="/cookies"
                  className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                >
                  {t("more")}
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});
